import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import type { Ich } from '@arbeitsrapporte/shared';
import { api, ApiFehler } from './api';
import { eingerichtet, SCOPES } from './anmeldung';
import { raeumeHashAdresseAuf, useRoute } from './router';
import { AuslastungSeite } from './seiten/Auslastung';
import { Bearbeiten } from './seiten/Bearbeiten';
import { Einsaetze } from './seiten/Einsaetze';
import { Einstellungen } from './seiten/Einstellungen';
import { Taetigkeitsarten } from './seiten/Taetigkeitsarten';
import { Liste, ZUR_FREIGABE } from './seiten/Liste';
import { Neu } from './seiten/Neu';
import { RapportSeite } from './seiten/Rapport';
import { UnterschriftSeite } from './seiten/Unterschrift';
import { Versand } from './seiten/Versand';
import { Testband } from './bausteine/Testband';

/**
 * Wer angemeldet ist – und ein Weg, es nachzuladen.
 *
 * Die Vorbelegung steckt in `ich`. Wer sie ändert, soll sie in der nächsten
 * Stundenzeile sehen – ohne dass die ganze Seite neu lädt und dabei
 * verliert, was gerade offen war.
 */
const IchKontext = createContext<{ ich: Ich; aktualisiere: () => Promise<void> } | null>(null);

export function useIch(): Ich {
  const wert = useContext(IchKontext);

  if (!wert) throw new Error('useIch außerhalb der Anmeldung.');

  return wert.ich;
}

export function useIchAktualisieren(): () => Promise<void> {
  const wert = useContext(IchKontext);

  if (!wert) throw new Error('useIchAktualisieren außerhalb der Anmeldung.');

  return wert.aktualisiere;
}

/**
 * Die Seiten vor dem Rahmen – Anmeldung, fehlende Einrichtung, fehlende
 * Rolle. Eine Karte in der Mitte, dieselbe Form wie in resad, der
 * Kalibrier-App, der Auftragsabwicklung und den Spesen: Es steht ein Satz
 * darauf und ein Knopf, das braucht keine Breite.
 */
function Mitte({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-6 text-center">
        <Testband className="-mx-6 -mt-6 mb-6 rounded-t-xl" />
        {children}
      </div>
    </main>
  );
}

function Anmeldung() {
  const { instance } = useMsal();

  if (!eingerichtet) {
    return (
      <Mitte>
        <h1 className="mb-2 text-2xl font-semibold">Arbeitsrapporte</h1>
        <p className="rounded border border-rose-300 bg-rose-50 px-4 py-2 text-sm text-rose-900">
          Die Anmeldung ist nicht eingerichtet: VITE_ENTRA_CLIENT_ID, VITE_ENTRA_TENANT_ID und
          VITE_ENTRA_SCOPE fehlen.
        </p>
      </Mitte>
    );
  }

  return (
    <Mitte>
      {/*
        Kein Zeichen über der Überschrift: Die Kachel stünde über einem Wort,
        das dasselbe sagt. Das „Ar" bleibt im Tab (`public/icon.svg`).
      */}
      <h1 className="mb-2 text-2xl font-semibold">Arbeitsrapporte</h1>
      <p className="mb-6 text-sm text-slate-600">Melden Sie sich mit Ihrem KALLMANN-Konto an.</p>
      <button
        type="button"
        onClick={() => instance.loginRedirect({ scopes: SCOPES })}
        className="knopf-haupt w-full"
      >
        Anmelden
      </button>
    </Mitte>
  );
}

function Seiten() {
  const route = useRoute();
  const ich = useIch();
  const istLeitung = ich.rollen.includes('leitung');
  /*
   * Die Leitung sieht alle Rapporte – die Liste heißt dann auch so, und der
   * Techniker steht an jeder Karte. „Meine" mit fremden Rapporten darin
   * wäre eine falsche Überschrift.
   */
  const liste = istLeitung ? (
    <Liste titel="Alle Rapporte" alle />
  ) : (
    <Liste titel="Meine Rapporte" />
  );

  switch (route.seite) {
    case 'neu':
      return <Neu />;
    /* Auch der Techniker – er sieht seine eigenen und trägt die Zusage ein. */
    case 'einsaetze':
      return <Einsaetze />;
    /* Auch der Techniker – er stellt hier seine eigene Vorbelegung ein. */
    case 'taetigkeitsarten':
      return <Taetigkeitsarten />;
    case 'rapport':
      return <RapportSeite id={route.id} />;
    case 'bearbeiten':
      return <Bearbeiten id={route.id} />;
    case 'unterschrift':
      return <UnterschriftSeite id={route.id} />;
    /* Nur die Disposition – fremde Kalender gehen den Techniker nichts an. */
    case 'auslastung':
      return istLeitung ? <AuslastungSeite /> : liste;
    case 'freigabe':
      return istLeitung ? <Liste titel="Zur Freigabe" status={ZUR_FREIGABE} alle /> : liste;
    case 'versand':
      return istLeitung ? <Versand /> : liste;
    /*
     * Auch für den Techniker – die Seite sagt ihm dann, dass sie die
     * Leitung führt. Ihn auf die Rapportliste zu werfen sähe aus, als
     * hätte der Verweis nicht funktioniert.
     */
    case 'einstellungen':
      return <Einstellungen />;
    default:
      return liste;
  }
}

/**
 * Ein Punkt in der Navigation.
 *
 * 44px hoch wie jeder Knopf (`stil.css`): Die Regel dort gilt für `button`
 * und `select`, nicht für Verweise – und die Leiste war mit ihrem alten
 * Innenabstand gut 32px hoch. Beim Kunden, mit einer Hand am Gerät, ist das
 * ein Glücksspiel.
 */
function Navigationslink({
  zu,
  aktiv,
  children,
}: {
  zu: string;
  aktiv: boolean;
  children: ReactNode;
}) {
  return (
    <a
      href={zu}
      className={`flex min-h-11 items-center px-1 text-slate-600 hover:text-slate-900 ${
        aktiv ? 'font-medium text-slate-900 underline underline-offset-4' : ''
      }`}
    >
      {children}
    </a>
  );
}

/**
 * Mit welcher Rolle jemand angemeldet ist – klein neben dem Namen, wie in
 * resad.
 *
 * Ein Menüpunkt, den man nicht sieht, sieht aus wie ein Fehler. Wer sich
 * wundert, warum „Freigabe" fehlt, soll hier nachsehen können, statt in
 * Entra zu suchen. Ein Rahmen und Versalien statt eines dritten Textteils:
 * Der Name aus Entra trägt oft schon einen Trenner („Jay Rongen | KALLMANN
 * engineering GmbH"), und eine weitere Angabe im selben Stil läse sich als
 * Firmenzusatz.
 */
function Rollenzeichen({ rollen }: { rollen: readonly string[] }) {
  if (rollen.length === 0) {
    return null;
  }

  return (
    <span
      title={`Angemeldet als ${rollen.join(', ')}`}
      className="max-w-32 truncate rounded border border-slate-300 px-2 py-0.5 text-xs tracking-wide text-slate-500 uppercase sm:max-w-none"
    >
      {rollen.join(' · ')}
    </span>
  );
}

/**
 * Ein Navigationspunkt, wie der Rahmen ihn führt.
 *
 * `sichtbar` ist die Bedingung, unter der er erscheint – Bedienkomfort,
 * keine Sicherheitsgrenze: Die API weist ohne Rolle ohnehin ab.
 */
type Punkt = { zu: string; seite: string; name: string; sichtbar: boolean };

/**
 * Was nicht in die erste Reihe gehört.
 *
 * **Warum es zwei Ebenen gibt.** Vorher standen sieben Punkte flach
 * nebeneinander, sortiert nach dem Datenmodell: Meine, Neu, Einsätze,
 * Vorbelegung, Freigabe, Versand, Einstellungen. Vier davon sind
 * Tätigkeiten, drei sind Nachschlagewerke – und sie hatten dasselbe
 * Gewicht. Auf dem Telefon scrollte die Leiste waagerecht, und wer beim
 * Kunden steht, wischte an der Vorbelegung vorbei, um zu dem zu kommen,
 * was er tut.
 *
 * Dieselbe Zweiteilung wie in resad und der Auftragsabwicklung, aus
 * demselben Grund.
 *
 * `<details>` statt eines eigenen Aufklappers: Der Browser bringt Tastatur
 * und Vorlesehilfen schon mit. Was fehlt, ist das Schließen – nach einem
 * Klick auf einen Verweis, bei Escape und beim Griff daneben. Das steht
 * unten.
 */
function Mehr({ punkte, aktiv }: { punkte: Punkt[]; aktiv: string }) {
  const menue = useRef<HTMLDetailsElement>(null);
  const sichtbare = punkte.filter((p) => p.sichtbar);

  const schliesse = () => {
    if (menue.current) {
      menue.current.open = false;
    }
  };

  useEffect(() => {
    function beiTaste(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        schliesse();
      }
    }

    function beiDruck(e: PointerEvent) {
      if (menue.current?.open && !menue.current.contains(e.target as Node)) {
        schliesse();
      }
    }

    document.addEventListener('keydown', beiTaste);
    document.addEventListener('pointerdown', beiDruck);

    return () => {
      document.removeEventListener('keydown', beiTaste);
      document.removeEventListener('pointerdown', beiDruck);
    };
  }, []);

  if (sichtbare.length === 0) {
    return null;
  }

  return (
    <details ref={menue} className="relative shrink-0">
      {/*
        `list-none` samt Marker-Regel: Ohne sie steht ein Dreieck davor, das
        hier nichts erklärt – der Punkt heißt „Mehr", das ist deutlich genug.
      */}
      <summary className="flex min-h-11 cursor-pointer list-none items-center px-1 text-slate-600 marker:content-none hover:text-slate-900 [&::-webkit-details-marker]:hidden">
        Mehr ▾
      </summary>

      <ul
        aria-label="Weitere Seiten"
        onClick={schliesse}
        className="absolute right-0 z-20 mt-1 min-w-48 rounded border border-slate-200 bg-white py-1 shadow-lg"
      >
        {sichtbare.map((p) => (
          <li key={p.zu}>
            <a
              href={p.zu}
              className={`flex min-h-11 items-center px-4 text-slate-700 hover:bg-slate-100 ${
                aktiv === p.seite ? 'font-medium text-slate-900' : ''
              }`}
            >
              {p.name}
            </a>
          </li>
        ))}
      </ul>
    </details>
  );
}

function Rahmen() {
  const { instance } = useMsal();
  const ich = useIch();
  const route = useRoute();
  const istLeitung = ich.rollen.includes('leitung');

  /*
   * Was jemand **tut** – vorn, immer sichtbar. Der eigene Rapport, ein
   * neuer, die Einsätze, und für die Leitung Auslastung und Freigabe: Das
   * ist der Arbeitstag. Fünf Punkte passen auch auf ein Telefon
   * nebeneinander – und die Leiste scrollt, wenn nicht.
   */
  const taetigkeiten: Punkt[] = [
    { zu: '/', seite: 'liste', name: istLeitung ? 'Rapporte' : 'Meine', sichtbar: true },
    { zu: '/neu', seite: 'neu', name: 'Neu', sichtbar: true },
    { zu: '/einsaetze', seite: 'einsaetze', name: 'Einsätze', sichtbar: true },
    /*
     * Gleich neben den Einsätzen: Wer disponiert, schaut hier nach, wer
     * Leerlauf hat, und plant dann dort.
     */
    { zu: '/auslastung', seite: 'auslastung', name: 'Auslastung', sichtbar: istLeitung },
    { zu: '/freigabe', seite: 'freigabe', name: 'Freigabe', sichtbar: istLeitung },
  ];

  /*
   * Was jemand **nachschlägt oder führt** – dahinter, unter „Mehr".
   *
   * Der Versand ist eine Arbeitsliste, die meistens leer ist; die
   * Vorbelegung und die Einstellungen werden eingerichtet und nicht
   * bedient. Alle drei standen vorn und nahmen Plätze weg, die auf ein
   * Telefon passen.
   *
   * „Vorbelegung" und nicht „Verrechnung": Der Techniker sucht dort das,
   * was in seiner Stundenzeile vorbelegt steht – nicht die Fakturierung.
   */
  const nachschlagen: Punkt[] = [
    { zu: '/versand', seite: 'versand', name: 'Versand', sichtbar: istLeitung },
    { zu: '/taetigkeitsarten', seite: 'taetigkeitsarten', name: 'Vorbelegung', sichtbar: true },
    /*
     * Ganz hinten, und nur für die Leitung: Hier wird eingerichtet, nicht
     * gearbeitet. Ein Techniker, der den Pfad von Hand eintippt, bekommt
     * von der API 403 – der fehlende Punkt ist Bedienkomfort, keine
     * Sicherheitsgrenze.
     */
    { zu: '/einstellungen', seite: 'einstellungen', name: 'Einstellungen', sichtbar: istLeitung },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Testband />
      <header className="border-b border-slate-200 bg-white">
        {/*
          Auf dem Telefon zwei Zeilen: oben Name und Abmelden, darunter die
          Navigation. Nebeneinander passte beides nicht, und die Kopfzeile
          wurde abgeschnitten. Ab `sm` steht wieder alles nebeneinander.
        */}
        <div className="mx-auto max-w-inhalt px-4 py-2 sm:px-6 sm:py-3">
          <div className="flex items-center justify-between gap-3">
            <a href="/" className="truncate font-semibold text-slate-900">
              Arbeitsrapporte
            </a>

            <div className="flex min-w-0 shrink items-center gap-2 text-sm sm:gap-3">
              {/* Der Name tritt auf dem Telefon zurück – der Platz gehört der Rolle daneben. */}
              <span
                className="hidden truncate text-slate-600 sm:inline"
                title={ich.mitarbeiter.email}
              >
                {ich.mitarbeiter.name}
              </span>
              <Rollenzeichen rollen={ich.rollen} />
              <button type="button" onClick={() => instance.logoutRedirect()} className="knopf">
                Abmelden
              </button>
            </div>
          </div>

          {/*
            Auf schmalen Geräten waagerecht scrollbar statt umgebrochen: Eine
            Navigation, die in zwei Zeilen springt, schiebt bei jedem
            Rollenwechsel den Inhalt darunter.

            „Mehr" steht ausserhalb des scrollenden Teils: Ein Element mit
            `overflow-x-auto` schneidet auch senkrecht ab, das aufgeklappte
            Menü lag auf dem Telefon unsichtbar hinter der Leiste, und
            hochkant war „Mehr" selbst aus dem Bild gescrollt.
          */}
          <nav
            aria-label="Hauptnavigation"
            className="-mx-4 flex items-center gap-4 px-4 pt-2 text-sm whitespace-nowrap sm:mx-0 sm:px-0 sm:pt-0"
          >
            <div className="flex min-w-0 flex-1 items-center gap-4 overflow-x-auto sm:overflow-visible">
              {taetigkeiten
                .filter((p) => p.sichtbar)
                .map((p) => (
                  <Navigationslink key={p.zu} zu={p.zu} aktiv={route.seite === p.seite}>
                    {p.name}
                  </Navigationslink>
                ))}
            </div>

            <Mehr punkte={nachschlagen} aktiv={route.seite} />
          </nav>
        </div>
      </header>

      {/*
        Unten bleibt Platz – `pb-24`. Auf dem Rapport steht der Knopf, der
        abschließt, als letztes Element im Dokument; klebte er am unteren
        Rand, wäre er auf dem Telefon kaum zu treffen.
      */}
      <main className="mx-auto max-w-inhalt px-4 pt-4 pb-24 sm:px-6 sm:pt-6">
        <Seiten />
      </main>
    </div>
  );
}

function Angemeldet() {
  const [ich, setIch] = useState<Ich | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const aktualisiere = useCallback(
    () =>
      api
        .ich()
        .then(setIch)
        .catch((f: unknown) => {
          setFehler(
            f instanceof ApiFehler && f.status === 403
              ? 'Dem Konto ist in Entra keine Rapport-Rolle zugewiesen.'
              : f instanceof Error
                ? f.message
                : 'Die API ist nicht erreichbar.',
          );
        }),
    [],
  );

  useEffect(() => {
    void aktualisiere();
  }, [aktualisiere]);

  if (fehler) {
    return (
      <Mitte>
        <h1 className="mb-2 text-2xl font-semibold">Kein Zugriff</h1>
        <p className="text-sm text-slate-600">{fehler}</p>
      </Mitte>
    );
  }

  if (!ich) {
    return (
      <Mitte>
        <p className="text-sm text-slate-600">Anmeldung wird geprüft …</p>
      </Mitte>
    );
  }

  return (
    <IchKontext.Provider value={{ ich, aktualisiere }}>
      <Rahmen />
    </IchKontext.Provider>
  );
}

export function App() {
  const angemeldet = useIsAuthenticated();

  useEffect(() => {
    raeumeHashAdresseAuf();
  }, []);

  return angemeldet ? <Angemeldet /> : <Anmeldung />;
}
